import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { 
  Newspaper, 
  Award, 
  FileText, 
  Briefcase, 
  TrendingUp,
  TrendingDown,
  ArrowRight,
  Building,
  FileArchive,
  Zap,
  Activity,
  Users,
  ChevronRight,
  Clock,
  AlertTriangle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Link } from 'react-router-dom';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Dashboard = () => {
  const { user, isCittadino, isSindacatoAdmin, canPublishGazzetta, canViewScontrini, hasAzienda } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentNews, setRecentNews] = useState([]);
  const [myCVs, setMyCVs] = useState([]);
  const [oreLavoro, setOreLavoro] = useState(null);
  const [meiWarns, setMeiWarns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const requests = [
        axios.get(`${API_URL}/stats`).catch(() => ({ data: {} })),
        axios.get(`${API_URL}/gazzetta`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/ore-lavoro/mie`).catch(() => ({ data: { ore_settimanali: 0, minuti_settimanali: 0, ore_totali: 0, minuti_totali: 0 } })),
        axios.get(`${API_URL}/warn/miei`).catch(() => ({ data: [] }))
      ];
      
      if (isCittadino()) {
        requests.push(axios.get(`${API_URL}/curriculum/my`).catch(() => ({ data: [] })));
      }
      
      const responses = await Promise.all(requests);
      setStats(responses[0].data || {});
      setRecentNews((responses[1].data || []).slice(0, 3));
      setOreLavoro(responses[2].data || { ore_settimanali: 0, minuti_settimanali: 0, ore_totali: 0, minuti_totali: 0 });
      setMeiWarns(responses[3].data || []);
      
      if (isCittadino() && responses[4]) {
        setMyCVs(responses[4].data || []);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(value);
  };

  const getRuoloDisplay = () => {
    if (user?.ruolo === 'SINDACATO_ADMIN') return 'Admin Sindacato';
    if (user?.ruolo_azienda_nome) return user.ruolo_azienda_nome;
    if (user?.ruolo === 'STAFF') return 'Staff';
    return user?.ruolo || '';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="relative">
          <div className="w-12 h-12 rounded-full border-2 border-slate-200 border-t-slate-900 animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page" className="space-y-8">
      {/* Header with futuristic style */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-8 text-white">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
            backgroundSize: '32px 32px'
          }} />
        </div>
        <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-cyan-500/20 to-transparent rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-violet-500/20 to-transparent rounded-full blur-3xl" />
        
        <div className="relative z-10">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-white/80 text-xs font-medium mb-3">
                <Activity className="w-3 h-3" />
                SISTEMA ATTIVO
              </div>
              <h1 className="text-3xl font-bold tracking-tight">
                Bentornato, {user?.nome}
              </h1>
              <div className="flex items-center gap-3 mt-2">
                <span className="px-3 py-1 text-xs font-semibold bg-white/20 rounded-full backdrop-blur-sm">
                  {getRuoloDisplay()}
                </span>
                {user?.azienda_nome && (
                  <span className="px-3 py-1 text-xs font-semibold bg-cyan-500/30 rounded-full backdrop-blur-sm">
                    {user.azienda_nome}
                  </span>
                )}
              </div>
            </div>
            <div className="flex gap-3">
              {isCittadino() ? (
                <Link to="/curriculum">
                  <Button data-testid="quick-add-cv" className="bg-white text-slate-900 hover:bg-slate-100 rounded-xl font-semibold shadow-lg shadow-white/20">
                    <FileText className="w-4 h-4 mr-2" />
                    Crea CV
                  </Button>
                </Link>
              ) : canPublishGazzetta() ? (
                <Link to="/gazzetta">
                  <Button data-testid="quick-add-news" className="bg-white text-slate-900 hover:bg-slate-100 rounded-xl font-semibold shadow-lg shadow-white/20">
                    <Newspaper className="w-4 h-4 mr-2" />
                    Nuova Notizia
                  </Button>
                </Link>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {/* Ore Online e Warns */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Ore Online - sempre visibile */}
        <Card className="bg-gradient-to-br from-blue-50 to-cyan-50 border-blue-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-blue-800 flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Le Tue Ore Online
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-3 bg-white/60 rounded-lg">
                <p className="text-2xl font-bold text-blue-700">
                  {oreLavoro?.ore_settimanali || 0}h {oreLavoro?.minuti_settimanali || 0}m
                </p>
                <p className="text-xs text-blue-600">Questa Settimana</p>
              </div>
              <div className="text-center p-3 bg-white/60 rounded-lg">
                <p className="text-2xl font-bold text-cyan-700">
                  {oreLavoro?.ore_totali || 0}h {oreLavoro?.minuti_totali || 0}m
                </p>
                <p className="text-xs text-cyan-600">Totale</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Warns */}
        {meiWarns.length > 0 && (
          <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-amber-800 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                Avvertimenti ({meiWarns.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {meiWarns.map((warn, idx) => (
                  <div key={warn.id || idx} className="p-2 bg-white/60 rounded-lg text-sm">
                    <p className="text-amber-900">{warn.messaggio}</p>
                    <p className="text-xs text-amber-600 mt-1">
                      {new Date(warn.data).toLocaleDateString('it-IT')}
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Stats Grid - Citizen View */}
      {isCittadino() && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="group relative overflow-hidden border-0 bg-white shadow-lg shadow-slate-200/50 hover:shadow-xl transition-all duration-300" data-testid="stat-my-cv">
            <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-blue-50 to-blue-100 rounded-full -translate-y-8 translate-x-8 group-hover:scale-110 transition-transform" />
            <CardHeader className="flex flex-row items-center justify-between pb-2 relative">
              <CardTitle className="text-sm font-semibold text-slate-500 uppercase tracking-wider">I Miei CV</CardTitle>
              <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
            </CardHeader>
            <CardContent className="relative">
              <div className="text-4xl font-bold text-slate-900">{myCVs.length}</div>
              <p className="text-sm text-slate-500 mt-1">Curriculum inseriti</p>
            </CardContent>
          </Card>

          <Card className="group relative overflow-hidden border-0 bg-white shadow-lg shadow-slate-200/50 hover:shadow-xl transition-all duration-300" data-testid="stat-concorsi">
            <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-amber-50 to-amber-100 rounded-full -translate-y-8 translate-x-8 group-hover:scale-110 transition-transform" />
            <CardHeader className="flex flex-row items-center justify-between pb-2 relative">
              <CardTitle className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Concorsi Aperti</CardTitle>
              <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                <Award className="w-5 h-5 text-amber-600" />
              </div>
            </CardHeader>
            <CardContent className="relative">
              <div className="text-4xl font-bold text-slate-900">{stats?.concorsi_aperti || 0}</div>
              <p className="text-sm text-slate-500 mt-1">Posizioni disponibili</p>
            </CardContent>
          </Card>

          <Card className="group relative overflow-hidden border-0 bg-white shadow-lg shadow-slate-200/50 hover:shadow-xl transition-all duration-300" data-testid="stat-gazzetta">
            <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-slate-50 to-slate-100 rounded-full -translate-y-8 translate-x-8 group-hover:scale-110 transition-transform" />
            <CardHeader className="flex flex-row items-center justify-between pb-2 relative">
              <CardTitle className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Notizie</CardTitle>
              <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center">
                <Newspaper className="w-5 h-5 text-slate-600" />
              </div>
            </CardHeader>
            <CardContent className="relative">
              <div className="text-4xl font-bold text-slate-900">{stats?.gazzetta_count || 0}</div>
              <p className="text-sm text-slate-500 mt-1">Articoli in Gazzetta</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Stats Grid - Staff View */}
      {!isCittadino() && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="group relative overflow-hidden border-0 bg-white shadow-lg shadow-slate-200/50 hover:shadow-xl transition-all duration-300" data-testid="stat-gazzetta">
              <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-slate-50 to-slate-100 rounded-full -translate-y-6 translate-x-6 group-hover:scale-110 transition-transform" />
              <CardHeader className="flex flex-row items-center justify-between pb-2 relative">
                <CardTitle className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Notizie</CardTitle>
                <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center">
                  <Newspaper className="w-5 h-5 text-slate-600" />
                </div>
              </CardHeader>
              <CardContent className="relative">
                <div className="text-4xl font-bold text-slate-900">{stats?.gazzetta_count || 0}</div>
              </CardContent>
            </Card>

            <Card className="group relative overflow-hidden border-0 bg-white shadow-lg shadow-slate-200/50 hover:shadow-xl transition-all duration-300" data-testid="stat-concorsi">
              <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-amber-50 to-amber-100 rounded-full -translate-y-6 translate-x-6 group-hover:scale-110 transition-transform" />
              <CardHeader className="flex flex-row items-center justify-between pb-2 relative">
                <CardTitle className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Concorsi Aperti</CardTitle>
                <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                  <Award className="w-5 h-5 text-amber-600" />
                </div>
              </CardHeader>
              <CardContent className="relative">
                <div className="text-4xl font-bold text-slate-900">{stats?.concorsi_aperti || 0}</div>
              </CardContent>
            </Card>

            <Card className="group relative overflow-hidden border-0 bg-white shadow-lg shadow-slate-200/50 hover:shadow-xl transition-all duration-300" data-testid="stat-curriculum">
              <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-blue-50 to-blue-100 rounded-full -translate-y-6 translate-x-6 group-hover:scale-110 transition-transform" />
              <CardHeader className="flex flex-row items-center justify-between pb-2 relative">
                <CardTitle className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Curriculum</CardTitle>
                <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                  <FileText className="w-5 h-5 text-blue-600" />
                </div>
              </CardHeader>
              <CardContent className="relative">
                <div className="text-4xl font-bold text-slate-900">{stats?.curriculum_count || 0}</div>
              </CardContent>
            </Card>

            <Card className="group relative overflow-hidden border-0 bg-white shadow-lg shadow-slate-200/50 hover:shadow-xl transition-all duration-300" data-testid="stat-ldl">
              <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-purple-50 to-purple-100 rounded-full -translate-y-6 translate-x-6 group-hover:scale-110 transition-transform" />
              <CardHeader className="flex flex-row items-center justify-between pb-2 relative">
                <CardTitle className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Domande in Attesa</CardTitle>
                <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                  <Briefcase className="w-5 h-5 text-purple-600" />
                </div>
              </CardHeader>
              <CardContent className="relative">
                <div className="text-4xl font-bold text-slate-900">{stats?.ldl_pending || 0}</div>
              </CardContent>
            </Card>
          </div>

          {/* Admin Extra Stats */}
          {isSindacatoAdmin() && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="group relative overflow-hidden border-0 bg-white shadow-lg shadow-slate-200/50 hover:shadow-xl transition-all duration-300" data-testid="stat-aziende">
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-full -translate-y-12 translate-x-12 group-hover:scale-110 transition-transform" />
                <CardHeader className="flex flex-row items-center justify-between pb-2 relative">
                  <CardTitle className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Aziende</CardTitle>
                  <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                    <Building className="w-6 h-6 text-emerald-600" />
                  </div>
                </CardHeader>
                <CardContent className="relative">
                  <div className="text-5xl font-bold text-slate-900">{stats?.aziende_count || 0}</div>
                  <Link to="/gestione-aziende" className="inline-flex items-center gap-1 mt-3 text-sm text-slate-600 hover:text-slate-900 font-medium group/link">
                    Gestisci aziende 
                    <ChevronRight className="w-4 h-4 group-hover/link:translate-x-1 transition-transform" />
                  </Link>
                </CardContent>
              </Card>

              <Card className="group relative overflow-hidden border-0 bg-white shadow-lg shadow-slate-200/50 hover:shadow-xl transition-all duration-300" data-testid="stat-documenti">
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-full -translate-y-12 translate-x-12 group-hover:scale-110 transition-transform" />
                <CardHeader className="flex flex-row items-center justify-between pb-2 relative">
                  <CardTitle className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Documenti</CardTitle>
                  <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
                    <FileArchive className="w-6 h-6 text-indigo-600" />
                  </div>
                </CardHeader>
                <CardContent className="relative">
                  <div className="text-5xl font-bold text-slate-900">{stats?.documenti_count || 0}</div>
                  <Link to="/documenti" className="inline-flex items-center gap-1 mt-3 text-sm text-slate-600 hover:text-slate-900 font-medium group/link">
                    Gestisci documenti 
                    <ChevronRight className="w-4 h-4 group-hover/link:translate-x-1 transition-transform" />
                  </Link>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Financial Overview */}
          {hasAzienda() && canViewScontrini() && (
            <Card className="relative overflow-hidden border-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white" data-testid="azienda-balance">
              <div className="absolute inset-0 opacity-10">
                <div className="absolute inset-0" style={{
                  backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
                  backgroundSize: '24px 24px'
                }} />
              </div>
              <CardHeader className="border-b border-white/10 relative">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center backdrop-blur-sm">
                    <Building className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-xl font-bold">{stats?.user_azienda_nome}</CardTitle>
                    <span className="text-xs font-medium text-white/60 uppercase tracking-wider">
                      {stats?.user_azienda_tipo}
                    </span>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-6 relative">
                <div className="flex items-center gap-4">
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${stats?.azienda_saldo >= 0 ? 'bg-emerald-500/20' : 'bg-red-500/20'}`}>
                    {stats?.azienda_saldo >= 0 ? (
                      <TrendingUp className="w-7 h-7 text-emerald-400" />
                    ) : (
                      <TrendingDown className="w-7 h-7 text-red-400" />
                    )}
                  </div>
                  <div>
                    <p className="text-sm text-white/60 font-medium">Saldo Attuale</p>
                    <p className={`text-4xl font-bold ${stats?.azienda_saldo >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {formatCurrency(stats?.azienda_saldo || 0)}
                    </p>
                  </div>
                </div>
                <Link to="/scontrini" className="inline-flex items-center gap-2 mt-6 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-sm font-medium transition-colors">
                  Visualizza scontrini
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Recent News */}
      <Card className="border-0 bg-white shadow-lg shadow-slate-200/50" data-testid="recent-news">
        <CardHeader className="border-b border-slate-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center">
                <Zap className="w-5 h-5 text-slate-600" />
              </div>
              <CardTitle className="text-xl font-bold text-slate-900">Ultime Notizie</CardTitle>
            </div>
            <Link to="/gazzetta">
              <Button variant="outline" size="sm" className="rounded-xl font-medium hover:bg-slate-100">
                Vedi Tutte
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          {recentNews.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Newspaper className="w-8 h-8 text-slate-400" />
              </div>
              <p className="text-slate-500 font-medium">Nessuna notizia pubblicata</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {recentNews.map((news) => (
                <div key={news.id} className="py-4 first:pt-0 last:pb-0 hover:bg-slate-50/50 -mx-6 px-6 transition-colors">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <h3 className="font-semibold text-slate-900 mb-1">{news.titolo}</h3>
                      <p className="text-sm text-slate-500 line-clamp-2">{news.contenuto}</p>
                    </div>
                    <span className="text-xs text-slate-400 whitespace-nowrap">
                      {new Date(news.created_at).toLocaleDateString('it-IT')}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
